import React, { useCallback, useEffect, useState } from 'react';
import { CircleHelp, Edit3, Eye, EyeOff, Plus, Save, Trash2, X } from 'lucide-react';
import Swal from 'sweetalert2';
import axiosInstance from '../api/AxiosInstance';
import { endpoints } from '../services/endpoints';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import { formatDate, formatTime } from '../helpers/DateFormatter';
import './FAQManagement.css';

const emptyForm = {
    question: '',
    answer: '',
    order: '',
    isActive: true,
};

const FAQManagement = () => {
    const [faqs, setFaqs] = useState([]);
    const [stats, setStats] = useState({ total: 0, activeCount: 0, inactiveCount: 0 });
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const loadFaqs = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter === 'active') params.isActive = 'true';
            if (statusFilter === 'inactive') params.isActive = 'false';

            const response = await axiosInstance.get(endpoints.faqs, { params });
            setFaqs(response?.data?.data?.faqs || []);
            setStats({
                total: response?.data?.data?.total || 0,
                activeCount: response?.data?.data?.activeCount || 0,
                inactiveCount: response?.data?.data?.inactiveCount || 0,
            });
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Unable to load FAQs');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        loadFaqs();
    }, [loadFaqs]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (faq) => {
        setEditingId(faq._id);
        setForm({
            question: faq.question || '',
            answer: faq.answer || '',
            order: faq.order ?? '',
            isActive: faq.isActive !== false,
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(emptyForm);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!form.question.trim() || !form.answer.trim()) {
            showErrorToast('Question and answer are required');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                question: form.question.trim(),
                answer: form.answer.trim(),
                isActive: form.isActive,
            };
            if (form.order !== '' && form.order !== null && form.order !== undefined) {
                payload.order = Number(form.order);
            }

            if (editingId) {
                await axiosInstance.put(`${endpoints.faqs}/${editingId}`, payload);
                showSuccessToast('FAQ updated successfully');
            } else {
                await axiosInstance.post(endpoints.faqs, payload);
                showSuccessToast('FAQ created successfully');
            }
            closeModal();
            await loadFaqs();
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Unable to save FAQ');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggle = async (faq) => {
        try {
            await axiosInstance.put(`${endpoints.faqs}/${faq._id}`, { isActive: !faq.isActive });
            showSuccessToast(faq.isActive ? 'FAQ hidden from app' : 'FAQ visible in app');
            await loadFaqs();
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Unable to update FAQ status');
        }
    };

    const handleDelete = async (faq) => {
        const result = await Swal.fire({
            title: 'Delete FAQ?',
            text: 'This FAQ will be removed permanently.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await axiosInstance.delete(`${endpoints.faqs}/${faq._id}`);
            showSuccessToast('FAQ deleted successfully');
            await loadFaqs();
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Unable to delete FAQ');
        }
    };

    return (
        <section className="faq-page">
            <div className="faq-heading">
                <div>
                    <h1>FAQ Management</h1>
                    <p>Add questions and answers that appear in the MatchMate app help section.</p>
                </div>
                <button type="button" onClick={openCreate}>
                    <Plus /> Add FAQ
                </button>
            </div>

            <div className="faq-stats">
                <article>
                    <div>
                        <span>Total FAQs</span>
                        <strong>{stats.total}</strong>
                    </div>
                    <CircleHelp />
                </article>
                <article>
                    <div>
                        <span>Visible in App</span>
                        <strong>{stats.activeCount}</strong>
                    </div>
                    <Eye />
                </article>
                <article>
                    <div>
                        <span>Hidden</span>
                        <strong>{stats.inactiveCount}</strong>
                    </div>
                    <EyeOff />
                </article>
            </div>

            <article className="faq-card">
                <div className="faq-card-title">
                    <h2>All FAQs</h2>
                    <label>
                        Status
                        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="">All</option>
                            <option value="active">Visible</option>
                            <option value="inactive">Hidden</option>
                        </select>
                    </label>
                </div>

                {loading ? (
                    <p className="faq-empty">Loading FAQs...</p>
                ) : faqs.length === 0 ? (
                    <p className="faq-empty">No FAQs found. Add your first question and answer.</p>
                ) : (
                    <div className="faq-list">
                        {faqs.map((faq) => (
                            <div className="faq-item" key={faq._id}>
                                <div className="faq-item-top">
                                    <div>
                                        <span className={`faq-badge ${faq.isActive ? 'active' : 'inactive'}`}>
                                            {faq.isActive ? 'Visible' : 'Hidden'}
                                        </span>
                                        <span className="faq-order">Order: {faq.order}</span>
                                        <span className="faq-date">
                                            {faq.createdAt ? `${formatDate(faq.createdAt)} at ${formatTime(faq.createdAt)}` : ''}
                                        </span>
                                    </div>
                                    <div className="faq-actions">
                                        <button type="button" title="Edit" onClick={() => openEdit(faq)}>
                                            <Edit3 />
                                        </button>
                                        <button
                                            type="button"
                                            title={faq.isActive ? 'Hide from app' : 'Show in app'}
                                            onClick={() => handleToggle(faq)}
                                        >
                                            {faq.isActive ? <EyeOff /> : <Eye />}
                                        </button>
                                        <button type="button" title="Delete" onClick={() => handleDelete(faq)}>
                                            <Trash2 />
                                        </button>
                                    </div>
                                </div>
                                <h3>{faq.question}</h3>
                                <p>{faq.answer}</p>
                            </div>
                        ))}
                    </div>
                )}
            </article>

            {showModal && (
                <div className="faq-modal-backdrop" onMouseDown={closeModal}>
                    <form className="faq-modal" onSubmit={handleSubmit} onMouseDown={(event) => event.stopPropagation()}>
                        <div className="faq-modal-title">
                            <h2>{editingId ? 'Edit FAQ' : 'Add FAQ'}</h2>
                            <button type="button" onClick={closeModal}><X /></button>
                        </div>

                        <label>
                            Question
                            <input
                                required
                                value={form.question}
                                onChange={(event) => setForm({ ...form, question: event.target.value })}
                                placeholder="e.g. How do I reset my password?"
                            />
                        </label>

                        <label>
                            Answer
                            <textarea
                                required
                                value={form.answer}
                                onChange={(event) => setForm({ ...form, answer: event.target.value })}
                                placeholder="Write a clear answer for the user..."
                                rows={5}
                            />
                        </label>

                        <div className="faq-modal-row">
                            <label>
                                Display order
                                <input
                                    type="number"
                                    min="0"
                                    value={form.order}
                                    onChange={(event) => setForm({ ...form, order: event.target.value })}
                                    placeholder="Auto"
                                />
                            </label>
                            <label className="faq-checkbox">
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                                />
                                Visible in app
                            </label>
                        </div>

                        <div className="faq-modal-actions">
                            <button type="button" className="secondary" onClick={closeModal}>Cancel</button>
                            <button type="submit" className="primary" disabled={submitting}>
                                <Save /> {submitting ? 'Saving...' : editingId ? 'Update FAQ' : 'Create FAQ'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </section>
    );
};

export default FAQManagement;
